import { CheckCircle2, Copy, Package, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { SEVERITY_COLORS } from '@root/utilities/pastelColors';

// "Fix available" card of the Findings overlay, from the findings graph API's `fix` object: the upgrade
// sentence with each target version's CVE diff, then the commands to apply and verify it.

const Version = ({ children }) => <code className="FindingDetails__Version">{children}</code>;

// "(CVE diff: ⊗ 13 Remain ⊘ 25 Resolved)" after a target version.
const CveDiff = ({ target }) => (
  <span className="FindingDetails__CveDiff">
    (CVE diff:
    <span className="FindingDetails__DiffCount">
      <XCircle size={13} color={SEVERITY_COLORS.critical} aria-hidden="true" />
      {target.remain_count ?? 0} Remain
    </span>
    <span className="FindingDetails__DiffCount">
      <CheckCircle2 size={13} color={SEVERITY_COLORS.success} aria-hidden="true" />
      {target.resolved_count ?? 0} Resolved
    </span>
    )
  </span>
);

const TargetVersion = ({ target }) => (
  <>
    <Version>{target.version}</Version> <CveDiff target={target} />
  </>
);

// The recommended and minimal targets are often the same version; it is then offered once.
const UpgradeTargets = ({ recommended, minimal }) => {
  if (recommended?.version && minimal?.version && recommended.version !== minimal.version) {
    return (
      <>
        to the latest recommended package version <TargetVersion target={recommended} /> or to minimal version{' '}
        <TargetVersion target={minimal} />
      </>
    );
  }
  const target = recommended?.version ? recommended : minimal;
  return (
    <>
      to version <TargetVersion target={target} />
    </>
  );
};

const copyToClipboard = async text => {
  await navigator.clipboard?.writeText(text);
  toast.info('Copied to clipboard!');
};

const Command = ({ label, command }) => (
  <div className="FindingDetails__Command">
    <span className="FindingDetails__CommandLabel">{label}</span>
    <div className="FindingDetails__CommandLine">
      <code>{command}</code>
      <Tooltip title="Copy command" isIcon={true}>
        <button
          type="button"
          className="FindingDetails__CopyButton"
          onClick={() => copyToClipboard(command)}
          aria-label={`Copy ${label.toLowerCase()}`}>
          <Copy size={14} aria-hidden="true" />
        </button>
      </Tooltip>
    </div>
  </div>
);

const PackageName = ({ name }) => (
  <span className="FindingDetails__Package">
    <Package size={13} aria-hidden="true" />
    {name}
  </span>
);

const FixAvailable = ({ fix }) => {
  const hasTarget = Boolean(fix?.recommended?.version || fix?.minimal?.version);

  if (!fix?.available || !hasTarget) {
    return (
      <p className="FindingDetails__Paragraph FindingDetails__Muted">
        {fix?.message ?? 'No fix is available for this vulnerability yet.'}
      </p>
    );
  }

  return (
    <>
      <p className="FindingDetails__Paragraph">
        Upgrade the <PackageName name={fix.package} />{' '}
        {fix.current_version && (
          <>
            current version <Version>{fix.current_version}</Version>{' '}
          </>
        )}
        <UpgradeTargets recommended={fix.recommended} minimal={fix.minimal} />
      </p>
      {fix.instruction && <p className="FindingDetails__Paragraph">{fix.instruction}</p>}
      {fix.command && <Command label="Upgrade command" command={fix.command} />}
      {fix.verify_command && <Command label="Verify command" command={fix.verify_command} />}
    </>
  );
};

export default FixAvailable;
